/**
 * page-editor-helpers — Pure helper functions for the page editor.
 * Extracted from PageEditorPage.tsx (Plan 150, Epic 03).
 *
 * No React dependencies — pure functions only.
 */

import type { AppearanceValue, SectionEnvelope } from '@zaviago/storefront-config'
import type { EditorSection } from './usePageSections'

/** Count all nested descendants recursively. */
export function countDescendants(s: EditorSection): number {
  if (!s.children?.length) return 0
  return s.children.reduce((n, c) => n + 1 + countDescendants(c), 0)
}

/** Editor prop key → AppearanceValue key mapping */
export const EDITOR_APPEARANCE_KEYS: Record<string, string> = {
  _bg: 'bg',
  _textColor: 'textColor',
  _padding: 'padding',
  _radius: 'radius',
  _shadow: 'shadow',
  _maxWidth: 'maxWidth',
  _margin: 'margin',
}

export function isAppearancePropKey(key: string): boolean {
  return key in EDITOR_APPEARANCE_KEYS
}

export function sectionHasResponsiveOverrides(section: EditorSection): boolean {
  const r = section.appearance?.responsive
  if (!r) return false
  const ro = r as Record<string, Record<string, unknown> | undefined>
  return (
    Object.keys(ro.tablet ?? {}).length > 0 ||
    Object.keys(ro.mobile ?? {}).length > 0
  )
}

/**
 * Build a complete AppearanceValue from editor section state + optional patch.
 * Desktop values come from section.props (underscore-prefixed),
 * responsive overrides from section.appearance.responsive.
 */
export function buildFullAppearance(
  section: EditorSection,
  patch?: Partial<SectionEnvelope>,
): AppearanceValue {
  const a: AppearanceValue = {}

  // Extract desktop values from editor props
  for (const [editorKey, appKey] of Object.entries(EDITOR_APPEARANCE_KEYS)) {
    const val = section.props[editorKey]
    if (val !== undefined) {
      ;(a as Record<string, unknown>)[appKey] = val
    }
  }

  // Apply desktop prop patch on top (e.g., user just changed _bg)
  if (patch?.props) {
    for (const [editorKey, val] of Object.entries(patch.props)) {
      const appKey = EDITOR_APPEARANCE_KEYS[editorKey]
      if (appKey && val !== undefined) {
        ;(a as Record<string, unknown>)[appKey] = val
      }
    }
  }

  // Carry over responsive overrides from committed appearance
  if (section.appearance?.responsive) {
    a.responsive = { ...section.appearance.responsive } as AppearanceValue['responsive']
  }

  // Apply responsive patch on top (for appearance-only patches)
  if (patch?.appearance?.responsive) {
    const patchR = patch.appearance.responsive as Record<string, Record<string, unknown> | undefined>
    const merged = { ...(a.responsive ?? {}) } as Record<string, Record<string, unknown> | undefined>
    for (const bp of ['tablet', 'mobile'] as const) {
      if (patchR[bp]) {
        const current = (merged[bp] ?? {}) as Record<string, unknown>
        const next: Record<string, unknown> = { ...current }
        for (const [k, v] of Object.entries(patchR[bp]!)) {
          if (v === null) delete next[k]
          else next[k] = v
        }
        if (Object.keys(next).length === 0) delete merged[bp]
        else merged[bp] = next
      }
    }
    if (Object.keys(merged).length === 0) delete a.responsive
    else a.responsive = merged as AppearanceValue['responsive']
  }

  return a
}
